import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { normalizarValorOrdenacao } from '../../../../../shared/helpers/ordenacao-tabela.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ColunaConfiguracao,
  ConfiguracaoColunasModalComponent,
} from '../../../../../shared/components/configuracao-colunas-modal/configuracao-colunas-modal.component';
import { FiltroExtracao } from '../../../../../shared/components/extracao-dados-modal/extracao-dados-modal.component';
import { EmptyListComponent } from '../../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '../../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { OrigemMovimentacaoEnum } from '../../../../../shared/enums/produto/origem-movimentacao.enum';
import { ControleEstoqueTipoMovimentoFiltroEnum } from '../../../../../shared/enums/produto/controle-estoque-tipo-movimento-filtro.enum';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { MaskService } from '../../../../../shared/services/mask.service';
import { LocalizacaoEstoque } from '../../../../../shared/services/models/produto/localizacao-estoque.model';
import { ControleEstoqueDetalheMovimentosRequest } from '../../../../../shared/services/requests/produto/controle-estoque-detalhe-movimentos.request';
import { ControleEstoqueDetalheMovimentoStruct } from '../../../../../shared/services/structs/produto/controle-estoque-detalhe-movimento.struct';
import { SeriesMovimentadasPopoverComponent } from '../../dialogs/series-movimentadas-popover/series-movimentadas-popover.component';
import { EditarMovimentacaoDialogComponent } from '../../dialogs/editar-movimentacao-dialog/editar-movimentacao-dialog.component';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { GetControleEstoqueDetalheMovimentosResponse } from '@shared/services/responses/produto/get-controle-estoque-detalhe-movimentos.response';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';

/** Indicador da coluna Origem: letras, cor (paleta --indicador-cor-*), texto do tooltip e rota do documento. */
interface IndicadorOrigemMovimento {
  sigla: string;
  cor: string;
  rotulo: string;
  rota: string | null;
}

/**
 * Letras, cores e tela de cada origem de movimento. Rota só onde o id_documento_origem gravado é o id que a
 * tela abre (conferido: NF-e de entrada, ordem de produção, transferência, inventário). NF-e de saída e pedido
 * de venda ainda não têm gravador: o link segue o mesmo padrão (idNFeSaida e idVenda). Pedido de compra
 * (Kanban), NFC-e (painel usa outro id), troca, manual e início de estoque não têm tela por id.
 */
const ORIGENS_MOVIMENTO: Record<number, { sigla: string; cor: string; nome: string; rota?: (id: number) => string }> = {
  [OrigemMovimentacaoEnum.InicioEstoque]: { sigla: 'II', cor: 'var(--fg-subtle)', nome: 'Início de estoque' },
  [OrigemMovimentacaoEnum.NotaFiscalEntrada]: {
    sigla: 'NE', cor: 'var(--indicador-cor-ne)', nome: 'NF-e de entrada',
    rota: id => `/suprimentos/nota-fiscal-entrada/documento/${id}`,
  },
  [OrigemMovimentacaoEnum.NFeSaida]: {
    sigla: 'NS', cor: 'var(--indicador-cor-ns)', nome: 'NF-e de saída',
    rota: id => `/vendas/nota-fiscal-saida/documento/${id}`,
  },
  [OrigemMovimentacaoEnum.NFCeSaida]: { sigla: 'NC', cor: 'var(--indicador-cor-nc)', nome: 'NFC-e' },
  [OrigemMovimentacaoEnum.OrdemProducao]: {
    sigla: 'OP', cor: 'var(--indicador-cor-e)', nome: 'Ordem de produção',
    rota: id => `/suprimentos/ordem-producao/${id}/visualizar`,
  },
  [OrigemMovimentacaoEnum.MovimentacaoManual]: { sigla: 'M', cor: 'var(--fg-subtle)', nome: 'Manual' },
  [OrigemMovimentacaoEnum.Inventario]: {
    sigla: 'IN', cor: 'var(--indicador-cor-ex)', nome: 'Inventário',
    rota: id => `/suprimentos/inventario/${id}`,
  },
  [OrigemMovimentacaoEnum.TrocaMercadoria]: { sigla: 'TM', cor: 'var(--indicador-cor-v)', nome: 'Troca de mercadoria' },
  [OrigemMovimentacaoEnum.PedidoVenda]: {
    sigla: 'PV', cor: 'var(--indicador-cor-f)', nome: 'Pedido de venda',
    rota: id => `/vendas/processo-venda/documento/${id}`,
  },
  [OrigemMovimentacaoEnum.Transferencia]: {
    sigla: 'TR', cor: 'var(--indicador-cor-c)', nome: 'Transferência',
    rota: id => `/suprimentos/transferencia-estoque/editar/${id}`,
  },
  [OrigemMovimentacaoEnum.PedidoCompra]: { sigla: 'PC', cor: 'var(--indicador-cor-s)', nome: 'Pedido de compra' },
};

enum PeriodoEnum {
  SemFiltro = 0,
  Ultimos30Dias = 1,
  DoMes = 2,
  DoIntervalo = 3,
}

@Component({
  selector: 'app-aba-movimentos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatBadgeModule,
    MatTooltipModule,
    MatSelectModule,
    MatSortModule,
    MatDatepickerModule,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
    AbntPipe,
  ],
  templateUrl: './aba-movimentos.component.html',
  styleUrls: ['./aba-movimentos.component.css'],
})
export class AbaMovimentosComponent implements OnInit, OnChanges {
  @Input() idProduto!: number;
  @Input() podeVisualizarCusto: boolean = false;
  @Input() listaLocalizacaoEstoque: LocalizacaoEstoque[] = [];
  @Input() ativa: boolean = false;

  @Output() filtroLocalChange = new EventEmitter<number | null>();
  @Output() executarStub = new EventEmitter<string>();
  @Output() movimentoAlterado = new EventEmitter<void>();

  public _tipoMovimentoFiltroEnum = ControleEstoqueTipoMovimentoFiltroEnum;

  public model!: FormGroup;
  public _isLoading: boolean = false;
  public _dataSource = new MatTableDataSource<ControleEstoqueDetalheMovimentoStruct>();
  public _paginaAtual: number = 0;
  public _ultimaPagina: boolean = false;
  public _carregouUmaVez: boolean = false;
  private pesquisaSubscription?: Subscription;

  public periodoSelecionado: PeriodoEnum = PeriodoEnum.Ultimos30Dias;
  public PeriodoEnum = PeriodoEnum;
  public periodoIntervaloDe: Date | null = null;
  public periodoIntervaloAte: Date | null = null;

  @ViewChild('periodoMenuTrigger') periodoMenuTrigger?: MatMenuTrigger;
  @ViewChild(MatSort) set sort(sort: MatSort | undefined) {
    if (!sort) return;
    this._dataSource.sort = sort;
  }

  private periodoSelecionadoAplicado: PeriodoEnum = PeriodoEnum.Ultimos30Dias;
  private periodoIntervaloDeAplicado: Date | null = null;
  private periodoIntervaloAteAplicado: Date | null = null;
  private periodoMenuFoiAplicado: boolean = false;

  public displayedColumns: string[] = [];
  public todosCampos: ColunaConfiguracao[] = [
    { def: 'dataHora', label: 'Data/Hora', visible: true, fixed: false },
    { def: 'operacao', label: 'Operação', visible: true, fixed: false },
    { def: 'localizacao', label: 'Localização', visible: true, fixed: false },
    { def: 'origem', label: 'Origem', visible: true, fixed: false },
    { def: 'clienteFornecedor', label: 'Cliente/Fornecedor', visible: true, fixed: false },
    { def: 'unidade', label: 'Unidade', visible: true, fixed: false },
    { def: 'valorUnitario', label: 'Valor unitário', visible: true, fixed: false },
    { def: 'custoMedio', label: 'Custo médio', visible: true, fixed: false },
    { def: 'reservado', label: 'Reservado', visible: true, fixed: false },
    { def: 'movimentado', label: 'Movimentado', visible: true, fixed: false },
    { def: 'disponivel', label: 'Disponível', visible: true, fixed: false },
    { def: 'actions', label: 'Ações', visible: true, fixed: true, pinnedEnd: true },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private controleEstoqueService: ControleEstoqueService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
      idLocalizacaoEstoque: [null],
      idTipoMovimentoFiltro: [ControleEstoqueTipoMovimentoFiltroEnum.SemFiltro],
    });

    this.aplicarLocalizacaoPadrao();

    this.setPeriodoUltimos30Dias();
    this.salvarPeriodoAplicado();
    this.configurarOrdenacao();

    this.atualizarColunasVisiveis();

    if (this.ativa && this.idProduto && !this._carregouUmaVez) {
      this.clickPesquisar();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['listaLocalizacaoEstoque']) {
      this.aplicarLocalizacaoPadrao();
    }

    if (!this.model) return;

    if (changes['ativa'] && this.ativa && !this._carregouUmaVez && this.idProduto) {
      this.clickPesquisar();
    }

    if (changes['podeVisualizarCusto']) {
      this.atualizarColunasVisiveis();
    }
  }

  // Filtro de Localização deveria abrir na localização padrão do sistema, não em "todos".
  private aplicarLocalizacaoPadrao(): void {
    if (!this.model || this.model.get('idLocalizacaoEstoque')?.value) return;

    const localPadrao = this.listaLocalizacaoEstoque?.find(l => l.isPadrao);
    if (!localPadrao) return;

    this.model.get('idLocalizacaoEstoque')?.setValue(localPadrao.idLocalizacaoEstoque);

    // A lista de locais chega depois da primeira busca: refaz mesmo com ela ainda em andamento,
    // senão a tela mostra o local padrão no filtro com a lista de todos os locais.
    if (this._carregouUmaVez || this._isLoading) {
      this.clickPesquisar();
    }
  }

  public clickPesquisar(): void {
    // Descarta a busca em andamento: o resultado dela é de filtros que já mudaram.
    this.pesquisaSubscription?.unsubscribe();
    this._isLoading = false;
    this._paginaAtual = 0;
    this._dataSource.data = [];
    this._ultimaPagina = false;
    this.pesquisar();
  }

  public getRequestAtual(): ControleEstoqueDetalheMovimentosRequest {
    return this.montarRequest();
  }

  public getFiltrosExtracao(): FiltroExtracao[] {
    const request = this.getRequestAtual();
    const colunasVisiveis = this.getColunasVisiveisSaida();
    const filtros: FiltroExtracao[] = [
      {
        label: 'Período',
        formControlName: 'periodoMovimentos',
        valor: this.periodoSelecionado,
        valorExibicao: this.periodoLabel,
      },
      {
        label: 'Colunas',
        formControlName: 'colunasVisiveis',
        valor: colunasVisiveis,
        valorExibicao: colunasVisiveis.join(', '),
      },
    ];

    if (request.searchText) {
      filtros.push({
        label: 'Pesquisa',
        formControlName: 'searchText',
        valor: request.searchText,
      });
    }

    if (request.idLocalizacaoEstoque) {
      filtros.push({
        label: 'Localização',
        formControlName: 'idLocalizacaoEstoque',
        valor: request.idLocalizacaoEstoque,
        valorExibicao: this.getDescricaoLocalizacao(request.idLocalizacaoEstoque),
      });
    }

    if (request.dataInicial) {
      filtros.push({
        label: 'Data inicial',
        formControlName: 'dataInicial',
        valor: request.dataInicial,
        valorExibicao: this.formatarDataExibicao(request.dataInicial),
      });
    }

    if (request.dataFinal) {
      filtros.push({
        label: 'Data final',
        formControlName: 'dataFinal',
        valor: request.dataFinal,
        valorExibicao: this.formatarDataExibicao(request.dataFinal),
      });
    }

    return filtros;
  }

  public getColunasVisiveisSaida(): string[] {
    return this.displayedColumns.filter(coluna => coluna !== 'actions');
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  public limparFiltros(): void {
    this.model.patchValue({
      pesquisa: '',
      idLocalizacaoEstoque: null,
      idTipoMovimentoFiltro: ControleEstoqueTipoMovimentoFiltroEnum.SemFiltro,
    });
    this.setPeriodoUltimos30Dias();
    this.salvarPeriodoAplicado();
    this.filtroLocalChange.emit(null);
    this.clickPesquisar();
  }

  public aplicarFiltros(): void {
    const idLocal = this.model.get('idLocalizacaoEstoque')?.value;
    this.filtroLocalChange.emit(idLocal ?? null);
    this.clickPesquisar();
  }

  public get qtdFiltrosAvancados(): number {
    if (!this.model) return 0;
    const tipoMovimento = this.model.get('idTipoMovimentoFiltro')?.value;
    return tipoMovimento && tipoMovimento !== ControleEstoqueTipoMovimentoFiltroEnum.SemFiltro ? 1 : 0;
  }

  private cloneDate(data: Date | null): Date | null {
    return data ? new Date(data) : null;
  }

  private salvarPeriodoAplicado(): void {
    this.periodoSelecionadoAplicado = this.periodoSelecionado;
    this.periodoIntervaloDeAplicado = this.cloneDate(this.periodoIntervaloDe);
    this.periodoIntervaloAteAplicado = this.cloneDate(this.periodoIntervaloAte);
  }

  private restaurarPeriodoAplicado(): void {
    this.periodoSelecionado = this.periodoSelecionadoAplicado;
    this.periodoIntervaloDe = this.cloneDate(this.periodoIntervaloDeAplicado);
    this.periodoIntervaloAte = this.cloneDate(this.periodoIntervaloAteAplicado);
  }

  public prepararPeriodoIntervalo(): void {
    this.periodoMenuFoiAplicado = false;

    if (
      this.periodoSelecionado === PeriodoEnum.DoIntervalo &&
      (!this.periodoIntervaloDe || !this.periodoIntervaloAte)
    ) {
      this.setPeriodoUltimos7DiasIntervalo();
    }
  }

  public onPeriodoMenuClosed(): void {
    if (!this.periodoMenuFoiAplicado) {
      this.restaurarPeriodoAplicado();
    }
  }

  public cancelarPeriodoIntervalo(trigger?: MatMenuTrigger): void {
    this.restaurarPeriodoAplicado();
    this.periodoMenuFoiAplicado = true;
    const menuTrigger = trigger ?? this.periodoMenuTrigger;
    setTimeout(() => menuTrigger?.closeMenu());
  }

  public aplicarPeriodo(trigger?: MatMenuTrigger): void {
    if (this.periodoSelecionado === PeriodoEnum.DoIntervalo) {
      if (!this.periodoIntervaloDe || !this.periodoIntervaloAte) {
        this.alertService.show(
          'Atenção',
          'Informe as duas datas do intervalo para aplicar o filtro.',
          AlertType.warning
        );
        return;
      }

      if (this.periodoIntervaloAte < this.periodoIntervaloDe) {
        this.alertService.show(
          'Atenção',
          'A data final não pode ser menor que a data inicial.',
          AlertType.warning
        );
        return;
      }
    }

    this.periodoMenuFoiAplicado = true;
    this.salvarPeriodoAplicado();
    this.clickPesquisar();
    const menuTrigger = trigger ?? this.periodoMenuTrigger;
    setTimeout(() => menuTrigger?.closeMenu());
  }

  public selecionarPeriodo(periodo: PeriodoEnum): void {
    this.periodoSelecionado = periodo;

    if (periodo === PeriodoEnum.DoIntervalo) {
      this.setPeriodoUltimos7DiasIntervalo();
    }
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const teclasPermitidas = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Enter',
    ];

    const isAtalho =
      (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());

    if (teclasPermitidas.includes(event.key) || isAtalho) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  public onDateBlur(event: FocusEvent, campo: 'de' | 'ate'): void {
    const input = event.target as HTMLInputElement;
    const valor = input?.value?.trim() || '';

    if (!valor) {
      this.atualizarCampoIntervalo(campo, null);
      return;
    }

    const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);

    if (apenasNumeros.length !== 8) {
      input.value = '';
      this.atualizarCampoIntervalo(campo, null);
      return;
    }

    const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
    input.value = formatado;

    const data = this.parseDataBr(formatado);
    if (!data) {
      this.atualizarCampoIntervalo(campo, null);
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
      return;
    }

    this.onPeriodoIntervaloChange(campo, data);
  }

  public onPeriodoIntervaloChange(campo: 'de' | 'ate', valor: Date | null): void {
    if (!valor) {
      this.atualizarCampoIntervalo(campo, null);
      return;
    }

    const dataNormalizada = new Date(valor);
    dataNormalizada.setHours(0, 0, 0, 0);
    this.atualizarCampoIntervalo(campo, dataNormalizada);

    if (
      this.periodoIntervaloDe &&
      this.periodoIntervaloAte &&
      this.periodoIntervaloAte < this.periodoIntervaloDe
    ) {
      if (campo === 'de') {
        this.periodoIntervaloAte = null;
        this.alertService.show(
          'Atenção',
          'A data final não pode ser menor que a data inicial.',
          AlertType.warning
        );
      } else {
        this.periodoIntervaloDe = null;
        this.alertService.show(
          'Atenção',
          'A data inicial não pode ser maior que a data final.',
          AlertType.warning
        );
      }
    }
  }

  public get periodoLabel(): string {
    switch (this.periodoSelecionado) {
      case PeriodoEnum.SemFiltro:
        return 'sem filtro';
      case PeriodoEnum.Ultimos30Dias:
        return 'últimos 30 dias';
      case PeriodoEnum.DoMes:
        return 'do mês';
      case PeriodoEnum.DoIntervalo:
        return 'do intervalo';
      default:
        return 'últimos 30 dias';
    }
  }

  private setPeriodoUltimos30Dias(): void {
    this.periodoSelecionado = PeriodoEnum.Ultimos30Dias;
    this.periodoIntervaloDe = null;
    this.periodoIntervaloAte = null;
  }

  private setPeriodoUltimos7DiasIntervalo(): void {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const seteDiasAtras = new Date(hoje);
    seteDiasAtras.setDate(hoje.getDate() - 6);

    this.periodoIntervaloDe = seteDiasAtras;
    this.periodoIntervaloAte = hoje;
  }

  private atualizarCampoIntervalo(campo: 'de' | 'ate', data: Date | null): void {
    if (campo === 'de') {
      this.periodoIntervaloDe = data;
      return;
    }

    this.periodoIntervaloAte = data;
  }

  private parseDataBr(valor: string): Date | null {
    const partes = valor.split('/');
    if (partes.length !== 3) return null;

    const dia = Number(partes[0]);
    const mes = Number(partes[1]);
    const ano = Number(partes[2]);

    if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
    if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;

    const data = new Date(ano, mes - 1, dia);
    data.setHours(0, 0, 0, 0);

    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;

    return data;
  }

  private getDatasPeriodo(): { inicio: Date | null; fim: Date | null } {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    switch (this.periodoSelecionado) {
      case PeriodoEnum.SemFiltro:
        return { inicio: null, fim: null };
      case PeriodoEnum.Ultimos30Dias: {
        const trintaDiasAtras = new Date(hoje);
        trintaDiasAtras.setDate(hoje.getDate() - 30);
        return { inicio: trintaDiasAtras, fim: hoje };
      }
      case PeriodoEnum.DoMes: {
        const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        return { inicio: inicioMes, fim: fimMes };
      }
      case PeriodoEnum.DoIntervalo:
        return { inicio: this.periodoIntervaloDe, fim: this.periodoIntervaloAte };
      default:
        return { inicio: null, fim: null };
    }
  }

  public configurarColunas(): void {
    const dialogRef = this.dialog.open(ConfiguracaoColunasModalComponent, {
      maxWidth: '700px',
      data: { colunas: this.getColunasPermitidas() },
    });

    dialogRef.afterClosed().subscribe((result: ColunaConfiguracao[]) => {
      if (!result) return;
      const colunasNaoPermitidas = this.todosCampos.filter(c => !this.colunaPermitida(c.def));
      this.todosCampos = [...result, ...colunasNaoPermitidas];
      this.atualizarColunasVisiveis();
    });
  }

  public abrirSeriesMovimentadas(row: ControleEstoqueDetalheMovimentoStruct): void {
    if (!row.listaSeriesMovimentadas?.length) return;
    this.dialog.open(SeriesMovimentadasPopoverComponent, {
      data: { descricaoOperacao: row.descricaoOperacao, series: row.listaSeriesMovimentadas },
      width: '520px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: '70vh',
      autoFocus: false,
    });
  }

  public podeEditarMovimento(row: ControleEstoqueDetalheMovimentoStruct): boolean {
    return row.idOrigemMovimentacao === OrigemMovimentacaoEnum.MovimentacaoManual;
  }

  public editarMovimento(row: ControleEstoqueDetalheMovimentoStruct): void {
    const dialogRef = this.dialog.open(EditarMovimentacaoDialogComponent, {
      data: { row },
      width: '560px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.alertService.show('Sucesso', 'Movimentação atualizada com sucesso.', AlertType.success);
        this.clickPesquisar();
        this.filtroLocalChange.emit(this.model.get('idLocalizacaoEstoque')?.value || null);
        this.movimentoAlterado.emit();
      }
    });
  }

  public indicadorOrigem(row: ControleEstoqueDetalheMovimentoStruct): IndicadorOrigemMovimento {
    const origem = ORIGENS_MOVIMENTO[row.idOrigemMovimentacao];
    const isManual = row.idOrigemMovimentacao === OrigemMovimentacaoEnum.MovimentacaoManual;
    const documento = !isManual && row.numeroDocumentoOrigem ? ` ${row.numeroDocumentoOrigem}` : '';

    if (!origem) {
      // Origem sem letra definida: primeira letra da descrição em cinza, sem link.
      const descricao = row.descricaoOrigem || 'Origem';
      return { sigla: descricao.substring(0, 1).toUpperCase(), cor: 'var(--fg-subtle)', rotulo: `${descricao}${documento}`, rota: null };
    }

    return {
      sigla: origem.sigla,
      cor: origem.cor,
      rotulo: `${origem.nome}${documento}`,
      rota: origem.rota && row.idDocumentoOrigem ? origem.rota(row.idDocumentoOrigem) : null,
    };
  }

  public abrirDocumentoOrigem(rota: string | null): void {
    if (!rota) return;
    window.open(rota, '_blank');
  }

  public podeExcluirMovimento(row: ControleEstoqueDetalheMovimentoStruct): boolean {
    return row.idOrigemMovimentacao === OrigemMovimentacaoEnum.MovimentacaoManual;
  }

  // Motivo pelo qual o botão Excluir está desabilitado (vazio = habilitado). Padrão contas-pagar/receber.
  public tooltipExcluir(row: ControleEstoqueDetalheMovimentoStruct): string {
    if (row.idOrigemMovimentacao !== OrigemMovimentacaoEnum.MovimentacaoManual)
      return 'Movimentação automática gerada por outro processo. Ajuste-a na tela de origem.';
    return '';
  }

  public excluirMovimento(_row: ControleEstoqueDetalheMovimentoStruct): void {
    if (!_row.idEstoqueMovimentacao) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Excluir movimentação',
        description: 'Esta ação é irreversível: a movimentação será removida do estoque e não poderá ser desfeita. Deseja continuar?',
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirm) {
        this.controleEstoqueService.excluirMovimentacao(_row.idEstoqueMovimentacao).subscribe((response) => {
          if (!response.isError) {
            this.alertService.show('Sucesso', 'Movimentação excluída com sucesso.', AlertType.success);
            this.clickPesquisar();
            // Optional: this.filtroLocalChange.emit() can be used to update header balances
            this.filtroLocalChange.emit(this.model.get('idLocalizacaoEstoque')?.value || null);
            this.movimentoAlterado.emit();
          } else {
            this.alertService.show('Aviso', response.errorDescription || 'Erro ao excluir.', AlertType.warning);
          }
        });
      }
    });
  }

  public colunaPermitida(coluna: string): boolean {
    if (coluna === 'custoMedio' && !this.podeVisualizarCusto) return false;
    return true;
  }

  private getColunasPermitidas(): ColunaConfiguracao[] {
    return this.todosCampos.filter(c => this.colunaPermitida(c.def));
  }

  private atualizarColunasVisiveis(): void {
    this.displayedColumns = this.getColunasPermitidas()
      .filter(c => c.visible)
      .map(c => c.def);
  }

  private pesquisar(): void {
    if (this._isLoading) return;
    this._isLoading = true;

    const request = this.montarRequest();

    this.pesquisaSubscription = this.controleEstoqueService.getMovimentos(this.idProduto, request, this._paginaAtual).subscribe({
      next: (response: GetControleEstoqueDetalheMovimentosResponse) => {
        this._isLoading = false;
        this._carregouUmaVez = true;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        const novos = response.list || [];
        const acumulado = this._paginaAtual === 0 ? novos : [...this._dataSource.data, ...novos];
        this._dataSource.data = acumulado;
        this._ultimaPagina = !novos.length || acumulado.length >= response.fullListSize;
      },
      error: err => {
        this._isLoading = false;
        this._carregouUmaVez = true;
        this.alertService.show('Erro inesperado', err?.message || err, AlertType.error);
      },
    });
  }

  private montarRequest(): ControleEstoqueDetalheMovimentosRequest {
    const request = new ControleEstoqueDetalheMovimentosRequest();
    const datas = this.getDatasPeriodo();

    request.searchText = this.model.get('pesquisa')?.value || undefined;
    request.idLocalizacaoEstoque = this.model.get('idLocalizacaoEstoque')?.value ?? null;
    request.idTipoMovimentoFiltro =
      this.model.get('idTipoMovimentoFiltro')?.value ?? ControleEstoqueTipoMovimentoFiltroEnum.SemFiltro;
    request.dataInicial = datas.inicio ? this.formatarData(datas.inicio) : null;
    request.dataFinal = datas.fim ? this.formatarData(datas.fim) : null;
    return request;
  }

  private formatarData(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private formatarDataExibicao(data: string): string {
    const partes = data.split('-');
    if (partes.length !== 3) return data;
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  }

  private getDescricaoLocalizacao(idLocalizacao: number): string {
    const local = this.listaLocalizacaoEstoque.find(x => x.idLocalizacaoEstoque === idLocalizacao);
    if (!local) return `${idLocalizacao}`;
    return `${local.codigo} - ${local.nome}`;
  }

  private getColunasVisiveisExportacao(): string[] {
    return this.getColunasVisiveisSaida();
  }

  private configurarOrdenacao(): void {
    this._dataSource.sortingDataAccessor = (item, property): string | number => {
      switch (property) {
        case 'dataHora':
          return normalizarValorOrdenacao(new Date(item.datetimeMovimentacao).getTime());
        case 'operacao':
          return normalizarValorOrdenacao(item.descricaoOperacao || '');
        case 'origem':
          return normalizarValorOrdenacao(item.numeroDocumentoOrigem || item.descricaoOrigem || '');
        case 'localizacao':
          return normalizarValorOrdenacao(item.localizacao || '');
        case 'clienteFornecedor':
          return normalizarValorOrdenacao(item.clienteFornecedor || item.descricaoOrigem || '');
        case 'unidade':
          return normalizarValorOrdenacao(item.unidade || '');
        case 'valorUnitario':
          return normalizarValorOrdenacao(item.valorUnitario ?? 0);
        case 'custoMedio':
          return normalizarValorOrdenacao(item.custoMedio ?? 0);
        case 'reservado':
          return normalizarValorOrdenacao(item.saldoReservado ?? 0);
        case 'movimentado':
          return normalizarValorOrdenacao(item.movimentado ?? 0);
        case 'disponivel':
          return normalizarValorOrdenacao(item.disponivel ?? 0);
        default:
          return normalizarValorOrdenacao((item as any)[property] ?? '');
      }
    };
  }
}
